import { QueryHookOptions } from '@apollo/client';
import {
  CollectionsFetchQuery,
  CollectionsFetchQueryVariables,
  useCollectionsFetchQuery,
} from 'lib-knowledge-management-common/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';

export const collectionsPageSize = 50;

/** What every caller asks for first. Exported for mocks to match on. */
export const collectionsFirstPage: CollectionsFetchQueryVariables = {
  offset: 0,
  limit: collectionsPageSize,
};

/**
 * The collections query, a page at a time.
 *
 * Pages merge into one list in the cache (the `collections` field policy), so
 * every caller sees what has been loaded so far, whoever loaded it.
 *
 * `fetchAll` keeps asking until every page is in, for callers that need the
 * whole list: a search, which only filters what is loaded, or an id lookup.
 */
export function usePaginatedCollections({
  fetchAll = false,
  ...options
}: Omit<
  QueryHookOptions<CollectionsFetchQuery, CollectionsFetchQueryVariables>,
  'variables'
> & { fetchAll?: boolean } = {}): ReturnType<
  typeof useCollectionsFetchQuery
> & {
  hasMore: boolean;
  fetchNextPage: () => Promise<void>;
} {
  const query = useCollectionsFetchQuery({
    ...options,
    variables: collectionsFirstPage,
  });
  const { fetchMore } = query;
  const loaded = query.data?.collections.results.length ?? 0;

  // The API gives no pagination info, so the end is where a page came back
  // short. Kept as a length rather than a flag: a create, delete or refetch
  // moves the list off it, and the next page is then asked for once more.
  const [endAt, setEndAt] = useState<number>();
  const hasMore = loaded >= collectionsPageSize && loaded !== endAt;

  const fetchNextPage = useCallback(
    () =>
      fetchMore({
        variables: { offset: loaded, limit: collectionsPageSize },
      }).then(({ data }) => {
        const pageLength = data.collections.results.length;
        if (pageLength < collectionsPageSize) setEndAt(loaded + pageLength);
      }),
    [fetchMore, loaded],
  );

  // The offset in flight, so a re-render while a page loads does not ask for
  // it twice.
  const requested = useRef<number>();
  useEffect(() => {
    if (!fetchAll || !hasMore || requested.current === loaded) return;
    requested.current = loaded;
    fetchNextPage().finally(() => {
      if (requested.current === loaded) requested.current = undefined;
    });
  }, [fetchAll, hasMore, loaded, fetchNextPage]);

  return { ...query, hasMore, fetchNextPage };
}
